// JSON-LD for the `market` surface (`/markets/[pubkey]`).
//
// Schema choice: WebPage + Question + Event. Three nodes:
//
//   1. `WebPage` — the indexable URL itself. Always emitted.
//   2. `Question` — the market is a Q&A entity. When the market is
//      resolved, we set `acceptedAnswer` (the killer field for Google's
//      Q&A rich result — markets become FAQ-style SERP entries).
//   3. `Event` — overlay describing the market as an event. Only
//      emitted when `dateCreated` (start) is supplied. Optional
//      `endDate` (resolution date) lets Google show event cards.
//
// `interactionStatistic` rolls up volume + trader count into the
// Question node — gives Google an engagement signal it can rank on.

import type { ShareSurface } from '@/lib/share/events';

import { organizationId, websiteId } from './site';
import type { JsonLdBuilderContext, JsonLdNode, JsonLdPayload } from './types';

function buildInteractionStatistic(
  volumeUsdc: number | undefined,
  traderCount: number | undefined,
): readonly JsonLdNode[] {
  const stats: JsonLdNode[] = [];
  if (typeof volumeUsdc === 'number' && Number.isFinite(volumeUsdc) && volumeUsdc > 0) {
    stats.push({
      '@context': 'https://schema.org',
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'TradeAction' },
      userInteractionCount: Math.round(volumeUsdc),
      // ALLOW-UNKNOWN: Schema.org's `unitText` is freeform; not a typed enum.
      unitText: 'USDC volume',
    });
  }
  if (typeof traderCount === 'number' && Number.isFinite(traderCount) && traderCount > 0) {
    stats.push({
      '@context': 'https://schema.org',
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'JoinAction' },
      userInteractionCount: traderCount,
      unitText: 'distinct traders',
    });
  }
  return stats;
}

export function buildMarketJsonLd(
  _surface: Extract<ShareSurface, { kind: 'market' }>,
  ctx: JsonLdBuilderContext,
): JsonLdPayload {
  const name = ctx.name ?? 'Opinion market';
  const interactionStats = buildInteractionStatistic(ctx.volumeUsdc, ctx.traderCount);

  const webPage: JsonLdNode = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${ctx.pageUrl}#webpage`,
    url: ctx.pageUrl,
    name,
    ...(ctx.description !== undefined ? { description: ctx.description } : {}),
    primaryImageOfPage: { '@type': 'ImageObject', url: ctx.ogImageUrl },
    isPartOf: { '@id': websiteId() },
    publisher: { '@id': organizationId() },
    ...(ctx.dateCreated !== undefined ? { dateCreated: ctx.dateCreated } : {}),
    ...(ctx.dateModified !== undefined ? { dateModified: ctx.dateModified } : {}),
  };

  const question: JsonLdNode = {
    '@context': 'https://schema.org',
    '@type': 'Question',
    '@id': `${ctx.pageUrl}#question`,
    name,
    ...(ctx.description !== undefined ? { text: ctx.description } : {}),
    url: ctx.pageUrl,
    image: ctx.ogImageUrl,
    author: { '@id': organizationId() },
    ...(ctx.dateCreated !== undefined ? { dateCreated: ctx.dateCreated } : {}),
    ...(ctx.dateModified !== undefined ? { dateModified: ctx.dateModified } : {}),
    ...(interactionStats.length > 0 ? { interactionStatistic: [...interactionStats] } : {}),
    // Resolved markets get `acceptedAnswer` — Google promotes these into
    // Q&A rich results in SERPs (the FAQ-strip-style result).
    ...(ctx.resolvedAnswer !== undefined && ctx.resolvedAnswer.length > 0
      ? {
          acceptedAnswer: {
            '@type': 'Answer',
            text: ctx.resolvedAnswer,
          },
        }
      : {}),
  };

  // Optional Event overlay — only emit when we have a start date. Lets
  // Google produce event-card SERPs and feature the resolution as a
  // calendar entry.
  const event: JsonLdNode | null =
    ctx.dateCreated !== undefined
      ? {
          '@context': 'https://schema.org',
          '@type': 'Event',
          '@id': `${ctx.pageUrl}#event`,
          name,
          ...(ctx.description !== undefined ? { description: ctx.description } : {}),
          url: ctx.pageUrl,
          image: ctx.ogImageUrl,
          startDate: ctx.dateCreated,
          ...(ctx.endDate !== undefined ? { endDate: ctx.endDate } : {}),
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
          eventStatus:
            ctx.resolvedAnswer !== undefined
              ? 'https://schema.org/EventScheduled'
              : 'https://schema.org/EventScheduled',
          location: {
            '@type': 'VirtualLocation',
            url: ctx.pageUrl,
          },
          organizer: { '@id': organizationId() },
        }
      : null;

  const nodes: JsonLdNode[] = [webPage, question];
  if (event !== null) nodes.push(event);
  return nodes;
}
